import { useState } from 'react';
import { motion } from 'framer-motion';
import { FaPaperPlane, FaMapMarkerAlt, FaPhoneAlt, FaEnvelope } from 'react-icons/fa';
import LogoImage from "../assets/bg5.png";
import { FiGithub, FiLinkedin, FiInstagram, FiFacebook } from 'react-icons/fi';

const Contact = () => {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);

      // Reset form after showing success message
      setTimeout(() => {
        setSubmitted(false);
        setFormState({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      }, 3000);
    }, 1500);
  };

  // Animation variants
  const containerVariant = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.5, 
        ease: [0.4, 0, 0.2, 1] 
      } 
    },
  };

  // New: Card hover animation
  

  // New: Input field focus animation
  const inputFocusVariant = (name) => ({
    initial: { borderColor: "transparent" },
    focus: { 
      scale: 1.02,
      transition: { type: "spring", stiffness: 400, damping: 20 }
    },
  });

  // New: Button animation
  const buttonVariants = {
    hover: { 
      scale: 1.05,
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    },
    tap: { 
      scale: 0.95 
    },
    // New: Submit animation
    submit: {
      scale: [1, 0.9, 1.1, 0.9, 1],
      transition: { 
        duration: 0.5 
      }
    }
  };

  // New: Success animation
  const successVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8 
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        duration: 0.5,
        delayChildren: 0.2,
        staggerChildren: 0.1
      }
    }
  };

  const successItemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        stiffness: 300, 
        damping: 20 
      }
    }
  };

  // New: Contact info item animation
  const contactInfoVariant = {
    hidden: { opacity: 0, x: -20 },
    visible: (custom) => ({
      opacity: 1,
      x: 0,
      transition: { 
        delay: custom * 0.2,
        duration: 0.5, 
        ease: "easeOut" 
      }
    })
  };

  // New: Social icon animation
  const socialIconVariant = {
    hover: { 
      scale: 1.2, 
      rotate: 12,
      
      transition: { 
        type: "spring", 
        stiffness: 500, 
        damping: 10 
      } 
    }
  };

  const contactInfo = [
    {
      icon: <FaMapMarkerAlt className="text-2xl text-red-500" />,
      title: "Location",
      content: "Available for remote work worldwide"
    },
    {
      icon: <FaEnvelope className="text-2xl text-blue-500" />,
      title: "Response Time",
      content: "Within 24-48 hours"
    },
    {
      icon: <FaPhoneAlt className="text-2xl text-green-500" />,
      title: "Availability",
      content: "Mon-Fri, 10am-12pm, 3pm-5pm"
    }
  ];

  const socialLinks = [
    {
          icon: FiGithub,
          href: "https://github.com/Jivan-Dahal",
          label: "GitHub",
          color: "text-[#181717] hover:text-[#0f0f0f] dark:text-white " // GitHub Black
        },
        {
          icon: FiLinkedin,
          href: "https://www.linkedin.com/in/jivan-dahal-611372334/",
          label: "LinkedIn",
          color: "text-[#0077B5] hover:text-[#005582]" // LinkedIn Blue
        },
        
        {
          icon: FiFacebook,
          href: "https://www.facebook.com/jivandahal2",
          label: "Facebook",
          color: "text-[#1877F2] hover:text-[#0f5dc8]" // Facebook Blue
        },
        {
          icon: FiInstagram,
          href: "https://www.instagram.com/jivan_dahal__",
          label: "Instagram",
          color: "text-[#E4405F] hover:text-[#c32e4a]" // Instagram Pink
        }
  ];

  return (
    <section
      id="contact"
      aria-labelledby="contact-heading"
      className="relative py-12 px-4 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 min-h-screen flex flex-col justify-center items-center overflow-hidden"
    >
      {/* Animated Background Elements */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ 
          opacity: 0.6, 
          scale: 1.2,
          x: [0, 30, 0, -30, 0],
          y: [0, -20, 0, 20, 0]
        }}
        transition={{ 
          duration: 20, 
          repeat: Infinity,
          repeatType: "reverse" 
        }}
        className="absolute top-0 left-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -z-10"
      />
      <motion.div 
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ 
          opacity: 0.6, 
          scale: 1.1,
          x: [0, -40, 0, 40, 0],
          y: [0, 30, 0, -30, 0]
        }}
        transition={{ 
          duration: 25, 
          repeat: Infinity,
          repeatType: "reverse" 
        }}
        className="absolute bottom-0 right-0 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl -z-10"
      />
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ 
          opacity: 0.5, 
          scale: 1.3,
          x: [0, 20, 0, -20, 0],
          y: [0, -15, 0, 15, 0]
        }}
        transition={{ 
          duration: 30, 
          repeat: Infinity,
          repeatType: "reverse" 
        }}
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl -z-10"
      />

      {/* Section Title */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="text-center mb-16 max-w-2xl mx-auto"
      >
        <motion.h2
          id="contact-heading"
          initial={{ backgroundPosition: "0% 50%" }}
          animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
          transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
          className="text-5xl md:text-6xl font-montserrat font-bold bg-gradient-to-r from-primary via-purple-500 to-blue-600 bg-clip-text text-transparent mb-4 bg-size-300"
        >
          Let's Connect
        </motion.h2>
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="text-lg text-gray-600 font-mono dark:text-gray-300"
        >
          Have a project in mind or just want to say hello? I'd love to hear from you.
        </motion.p>
      </motion.div>

      {/* Main Content */}
      <div className="w-full max-w-7xl mx-auto">
        <motion.div
          variants={containerVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid lg:grid-cols-5 gap-8"
        >
          {/* Left Side: Info Card */}
          <motion.div
            variants={itemVariant}
            whileHover="hover"
           
            className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-8 overflow-hidden relative z-10"
          >
            <div className="h-full flex flex-col">
              {/* Logo & Brand */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="mb-8"
              >
                <motion.img
                  src={LogoImage}
                  alt="Logo"
                  className="h-12 mb-6"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  whileHover={{ 
                    scale: 1.05,
                    transition: { type: "spring", stiffness: 300, damping: 10 }
                  }}
                />
                <motion.h3 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="text-2xl font-robotoMono font-bold text-gray-800 dark:text-white mb-4"
                >
                  Ready to Transform Your Ideas into Reality
                </motion.h3>
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="text-gray-600 font-mono dark:text-gray-200"
                >
                  Let's collaborate to create something amazing together. Fill out the form and I'll get back to you as soon as possible.
                </motion.p>
              </motion.div>

              {/* Contact Information */}
              <div className="space-y-6 mt-auto">
                {contactInfo.map((item, index) => (
                  <motion.div 
                    key={index} 
                    custom={index}
                    variants={contactInfoVariant}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    whileHover={{ 
                      x: 5,
                      transition: { type: "spring", stiffness: 400, damping: 10 }
                    }}
                    className="flex items-start space-x-4"
                  >
                    <motion.div 
                      whileHover={{ 
                        scale: 1.1,
                        rotate: 5,
                        backgroundColor: "rgba(109, 40, 217)" // Light primary color
                      }}
                      className="flex-shrink-0 bg-gray-100 dark:bg-gray-200 p-3 rounded-lg"
                    >
                      {item.icon}
                    </motion.div>
                    <div>
                      <h4 className="font-medium font-robotoMono text-gray-800 dark:text-white">{item.title}</h4>
                      <p className="text-gray-600 font-mono dark:text-gray-300">{item.content}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Social Media Links */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700"
              >
                <p className="mb-4 font-bold font-robotoMono text-gray-800 dark:text-white">Connect with me:</p>
                <div className="flex space-x-4">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      variants={socialIconVariant}
                      whileHover="hover"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ 
                        opacity: 1, 
                        scale: 1,
                        transition: { delay: 0.9 + index * 0.1, duration: 0.3 } 
                      }}
                      className={`${social.color}  duration-300`}
                      aria-label={social.label}
                    >
                      <social.icon className="w-7 h-7" />
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Side: Contact Form */}
          <motion.div
            variants={itemVariant}
            whileHover="hover"
          
            className="lg:col-span-3 bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-8 md:p-10 relative z-10"
          >
            {submitted ? (
              <motion.div
                variants={successVariants}
                initial="hidden"
                animate="visible"
                className="h-full flex flex-col items-center justify-center text-center py-12"
              >
                <motion.div 
                  variants={successItemVariants}
                  animate={{ 
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, 0, -5, 0]
                  }}
                  transition={{ 
                    duration: 0.8, 
                    ease: "easeInOut",
                    times: [0, 0.2, 0.5, 0.8, 1],
                    repeat: 1,
                    repeatDelay: 1
                  }}
                  className="w-20 h-20 bg-green-100 text-green-500 rounded-full flex items-center justify-center mb-6"
                >
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <motion.path 
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth="2" 
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </motion.div>
                <motion.h3 variants={successItemVariants} className="text-2xl font-robotoMono font-bold text-gray-800 dark:text-white mb-3">
                  Message Sent!
                </motion.h3>
                <motion.p variants={successItemVariants} className="text-gray-600 font-mono dark:text-gray-300 max-w-md">
                  Thank you for reaching out. I'll get back to you very soon!
                </motion.p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <motion.div variants={itemVariant} className="form-group">
                    <label htmlFor="name" className="block font-robotoMono text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">
                      Your Name:
                    </label>
                    <motion.div
                      variants={inputFocusVariant('name')}
                      animate={focusedField === 'name' ? 'focus' : 'initial'}
                    >
                      <input
                        id="name"
                        type="text"
                        name="name"
                        value={formState.name}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('name')}
                        onBlur={() => setFocusedField(null)}
                        required
                        className="w-full bg-gray-50 font-mono dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-5 py-4 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                        placeholder="John Doe"
                      />
                    </motion.div>
                  </motion.div>

                  <motion.div variants={itemVariant} className="form-group">
                    <label htmlFor="email" className="block font-robotoMono text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">
                      Email Address:
                    </label>
                    <motion.div
                      variants={inputFocusVariant('email')}
                      animate={focusedField === 'email' ? 'focus' : 'initial'}
                    >
                      <input
                        id="email"
                        type="email"
                        name="email"
                        value={formState.email}
                        onChange={handleChange}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField(null)}
                        required
                        className="w-full bg-gray-50 font-mono dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-5 py-4 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                        placeholder="you@example.com"
                      />
                    </motion.div>
                  </motion.div>
                </div>

                <motion.div variants={itemVariant} className="form-group">
                  <label htmlFor="subject" className="block font-robotoMono text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    Subject:
                  </label>
                  <motion.div
                    variants={inputFocusVariant('subject')}
                    animate={focusedField === 'subject' ? 'focus' : 'initial'}
                  >
                    <input
                      id="subject"
                      type="text"
                      name="subject"
                      value={formState.subject}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('subject')}
                      onBlur={() => setFocusedField(null)}
                      required
                      className="w-full bg-gray-50 font-mono dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-5 py-4 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                      placeholder="Project Inquiry"
                    />
                  </motion.div>
                </motion.div>

                <motion.div variants={itemVariant} className="form-group">
                  <label htmlFor="message" className="block font-robotoMono text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    Your Message:
                  </label>
                  <motion.div
                    variants={inputFocusVariant('message')}
                    animate={focusedField === 'message' ? 'focus' : 'initial'}
                  >
                    <textarea
                      id="message"
                      name="message"
                      value={formState.message}
                      onChange={handleChange}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => setFocusedField(null)}
                      rows={6}
                      required
                      className="w-full bg-gray-50 font-mono dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl px-5 py-4 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 resize-none"
                      placeholder="Tell me about your project, questions..."
                    />
                  </motion.div>
                </motion.div>

                <motion.div variants={itemVariant} className="mt-6">
                  <motion.button
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                    animate={isSubmitting ? "submit" : ""}
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-4 px-6 bg-gradient-to-r font-robotoMono text-xl  from-primary to-purple-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-300 flex items-center justify-center ${isSubmitting ? 'opacity-80' : ''}`}
                  >
                    {isSubmitting ? (
                      <motion.svg 
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="-ml-1 mr-3 h-5 w-5 text-white" 
                        xmlns="http://www.w3.org/2000/svg" 
                        fill="none" 
                        viewBox="0 0 24 24"
                      >
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </motion.svg>
                    ) : (
                      <motion.div 
                        initial={{ x: 0 }}
                        whileHover={{ x: -3, transition: { repeat: Infinity, repeatType: "mirror", duration: 0.5 } }}
                      >
                        <FaPaperPlane className="mr-2" />
                      </motion.div>
                    )}
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </motion.button>
                </motion.div>
              </form>
            )}

            {/* Form Decorative Elements */}
            <motion.div 
              animate={{ 
                scale: [1, 1.1, 1],
                opacity: [0.5, 0.7, 0.5],
                rotate: [0, 5, 0]
              }}
              transition={{ 
                duration: 8, 
                repeat: Infinity,
                repeatType: "reverse"
              }}
              className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-transparent to-primary/5 rounded-full -z-10"
            />
            <motion.div 
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.5, 0.8, 0.5],
                rotate: [0, -5, 0]
              }}
              transition={{ 
                duration: 10, 
                repeat: Infinity,
                repeatType: "reverse"
              }}
              className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-transparent to-blue-500/5 rounded-full -z-10"
            />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;